import React, { useEffect, useMemo, useState } from 'react';

function preloadDecodedImage(src) {
  return new Promise((resolve, reject) => {
    if (!src || typeof window === 'undefined') {
      reject(new Error('Missing image source'));
      return;
    }

    const image = new Image();
    image.decoding = 'async';

    const settle = () => {
      if (image.decode) {
        image.decode().catch(() => {}).then(resolve);
      } else {
        resolve();
      }
    };

    image.onload = settle;
    image.onerror = reject;
    image.src = src;

    if (image.complete && image.naturalWidth > 0) {
      settle();
    }
  });
}

function useDecodedImage(src) {
  const [displaySrc, setDisplaySrc] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    let frameId = null;

    setVisible(false);
    setDisplaySrc('');

    if (!src) {
      return undefined;
    }

    preloadDecodedImage(src)
      .then(() => {
        if (!active) return;
        setDisplaySrc(src);
        frameId = window.requestAnimationFrame(() => {
          if (active) {
            setVisible(true);
          }
        });
      })
      .catch(() => {
        if (!active) return;
        setDisplaySrc(src);
        frameId = window.requestAnimationFrame(() => {
          if (active) {
            setVisible(true);
          }
        });
      });

    return () => {
      active = false;
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [src]);

  return { displaySrc, visible };
}

function getBackgroundTarget(sources) {
  if (typeof window === 'undefined') {
    return { kind: 'desktop', src: sources.desktopSrc };
  }

  if (window.matchMedia('(max-width: 767px)').matches) {
    return { kind: 'mobile', src: sources.mobileSrc };
  }

  if (window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches) {
    return { kind: 'tablet', src: sources.tabletSrc };
  }

  return { kind: 'desktop', src: sources.desktopSrc };
}

export function DecodedArtImage({
  src,
  alt = '',
  aspectRatio = '1 / 1',
  className = '',
  style,
  ...props
}) {
  const { displaySrc, visible } = useDecodedImage(src);
  const mergedStyle = {
    ...style,
    '--decoded-art-aspect': aspectRatio,
  };

  return (
    <span
      {...props}
      className={`decoded-art ${visible ? 'is-visible' : ''} ${className}`.trim()}
      style={mergedStyle}
    >
      {displaySrc ? (
        <img
          src={displaySrc}
          alt={alt}
          className="decoded-art__image"
          decoding="async"
          draggable="false"
        />
      ) : null}
    </span>
  );
}

export function ResponsiveDecodedBackground({ mobileSrc, tabletSrc, desktopSrc }) {
  const sources = useMemo(
    () => ({ mobileSrc, tabletSrc, desktopSrc }),
    [desktopSrc, mobileSrc, tabletSrc]
  );
  const [target, setTarget] = useState(() => getBackgroundTarget(sources));
  const { displaySrc, visible } = useDecodedImage(target.src);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateTarget = () => {
      setTarget(getBackgroundTarget(sources));
    };

    const queries = [
      window.matchMedia('(max-width: 767px)'),
      window.matchMedia('(min-width: 768px) and (max-width: 1023px)'),
      window.matchMedia('(min-width: 1024px)'),
    ];

    queries.forEach((query) => {
      if (query.addEventListener) {
        query.addEventListener('change', updateTarget);
      } else {
        query.addListener(updateTarget);
      }
    });

    updateTarget();

    return () => {
      queries.forEach((query) => {
        if (query.removeEventListener) {
          query.removeEventListener('change', updateTarget);
        } else {
          query.removeListener(updateTarget);
        }
      });
    };
  }, [sources]);

  if (!displaySrc) {
    return null;
  }

  return (
    <img
      src={displaySrc}
      alt=""
      aria-hidden="true"
      className={`bg-gif ${target.kind} decoded-bg ${visible ? 'is-visible' : ''}`}
      decoding="async"
      draggable="false"
    />
  );
}
